import React, { useState, FC, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { BRAND, BRAND_DARK, BRAND_DARKER, formatDate, formatPanelLabel, formatInterpretation, isAbnormalInterp, panelInterpColor } from './healthWalletConstants';
import type { DetailEntry } from './healthWalletConstants';
import type { ChartData } from './healthWalletSlice';
import { ValueChart } from './ValueChart';
import { DocumentBlock } from './DocumentBlock';

// --- Detail Entry Row --- (`primary` = styling tier, decided by which server array the row came from)
// MOBILE (xs): inline "Label: value" (label bolded, colon via ::after) so a narrow screen doesn't crush the value
// into a sliver of a fixed-width 2nd column. DESKTOP (sm+): the original 2-column layout (label minWidth 130 | value).
const DetailRow: FC<{ entry: DetailEntry; last: boolean; primary: boolean }> = ({ entry, last, primary }) => {
  // MOBILE echo of the (now-hidden) compact header: the Date value takes the top date's accent color and
  // the Value row is bolded, so the two facts still read at a glance. Desktop keeps the neutral 2-column look.
  const baseColor = primary ? 'text.primary' : 'text.disabled';
  const isDate = entry.label === 'Date';
  const isValueRow = entry.label === 'Value';
  return (
    <Box sx={{ display: { xs: 'block', sm: 'flex' }, py: 0.75, borderBottom: last ? 'none' : '1px solid #f0f0f0' }}>
      <Typography component="span" variant="body2" sx={{
        fontWeight: 600, color: primary ? '#1c1c1e' : 'text.disabled', fontSize: primary ? '0.875rem' : '0.8rem',
        display: { xs: 'inline', sm: 'block' }, minWidth: { sm: 130 }, mr: { xs: 0.5, sm: 0 },
        '&::after': { content: { xs: '":"', sm: '""' } },
      }}>
        {entry.label}
      </Typography>
      <Typography component="span" variant="body2" sx={{
        wordBreak: 'break-word', fontSize: primary ? '0.875rem' : '0.8rem',
        color: isDate ? { xs: BRAND_DARKER, sm: baseColor } : baseColor,
        fontWeight: isValueRow ? { xs: 700, sm: 400 } : undefined,
        display: { xs: 'inline', sm: 'block' },
      }}>
        {entry.value}
      </Typography>
    </Box>
  );
};

// --- One timeline event row — React.memo'd ON PURPOSE (perf; do not inline back into the map).
// Expanding/collapsing writes selectedEvent state in EventTimeline; without the memo that re-rendered
// EVERY row (~8 emotion-styled elements each — thousands of style computations on big groups = the ~1s
// open/close delay). With it, a click re-renders exactly the row(s) whose props changed (the one
// closing, the one opening) + the chart dot. All props are kept referentially STABLE across parent
// renders: ev objects come from the memoized displayList, callbacks are useCallback'd, cardRefs is a
// stable ref object. Hover feedback is pure CSS (see EventTimeline header note).
interface EventRowProps {
  ev: any;
  origIdx: number;
  isFirst: boolean;
  isLast: boolean;
  isExpanded: boolean;
  showTechnical: boolean; // only ever true for the expanded row
  onSelect: (idx: number) => void;
  onToggleTechnical: () => void;
  cardRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  hideHeaderOnExpand: boolean; // hide the compact date/name header on expand (desktop too), for rows whose details repeat it
  groupHasReport: boolean;     // ANY row in this group has a report → render the View-report affordance on every row
}
const EventRow = React.memo(({ ev, origIdx, isFirst, isLast, isExpanded, showTechnical, onSelect, onToggleTechnical, cardRefs, hideHeaderOnExpand, groupHasReport }: EventRowProps) => {
  const rowHasReport = Array.isArray(ev.report) && ev.report.length > 0;
  // When the caller opts in (conditions visit-trail), the collapsed date/name header is hidden on expand at ALL
  // breakpoints — the expanded details.primary already repeats it (incl. its own Date row), so keeping the header
  // would double it. Default keeps the original behavior (hide on xs only, always show on desktop).
  const headerDisplay = hideHeaderOnExpand
    ? (isExpanded ? 'none' : 'block')
    : { xs: isExpanded ? 'none' : 'block', sm: 'block' };
  // details arrives READY-TO-RENDER from get_patient_resource_events ({primary,secondary} DetailEntry
  // rows — labels/dates/field-picking all built in SQL; the old FE extractDetailSummary is gone).
  const evDetail: { primary: DetailEntry[]; secondary: DetailEntry[] } | null = isExpanded
    ? (ev.details && (ev.details.primary?.length || ev.details.secondary?.length)
        ? { primary: ev.details.primary ?? [], secondary: ev.details.secondary ?? [] }
        : { primary: [{ label: 'Info', value: 'No additional details available.' }], secondary: [] })
    : null;
  return (
    <Box
      ref={(el: HTMLDivElement | null) => { cardRefs.current[origIdx] = el; }}
      sx={{ display: 'flex', position: 'relative', minHeight: isExpanded ? 'auto' : 52 }}
    >
      {/* Timeline rail */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
        {/* Vertical line segment above dot */}
        {!isFirst && <Box sx={{ width: 2, flex: 1, bgcolor: '#d7d3f0' }} />}
        {isFirst && <Box sx={{ flex: 1 }} />}
        {/* Dot */}
        <Box
          role="button"
          tabIndex={0}
          aria-label={`${ev.name || `Event ${origIdx + 1}`}, ${formatDate(ev.start)}${isExpanded ? ', expanded' : ''}`}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(origIdx); } }}
          sx={{
            width: isExpanded ? 16 : 10,
            height: isExpanded ? 16 : 10,
            borderRadius: '50%',
            bgcolor: isExpanded ? BRAND_DARKER : BRAND_DARK,
            border: '2px solid white',
            boxShadow: isExpanded ? `0 0 0 3px ${BRAND}` : '0 1px 3px rgba(0,0,0,0.12)',
            flexShrink: 0,
            cursor: 'pointer',
            zIndex: 2,
            // Pure-CSS hover feedback (browser-painted — no React state, no re-renders):
            '&:hover': { bgcolor: BRAND_DARKER, boxShadow: `0 0 0 3px ${BRAND}` },
            '&:focus-visible': { outline: `2px solid ${BRAND_DARKER}`, outlineOffset: 2 },
          }}
          onClick={() => onSelect(origIdx)}
        />
        {/* Vertical line segment below dot */}
        {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: '#d7d3f0' }} />}
        {isLast && <Box sx={{ flex: 1 }} />}
      </Box>

      {/* Event card. minWidth:0 is LOAD-BEARING: a flex item defaults to min-width:auto (= content min-content),
          which for the noWrap result line is the whole text → the card refuses to shrink and overflows/clips.
          minWidth:0 lets it shrink so the inner noWrap children ellipsize instead. */}
      <Box sx={{ flex: 1, minWidth: 0, pl: 1.5, py: 0.5 }}>
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-label={`${ev.name || `Event ${origIdx + 1}`}, ${formatDate(ev.start)}`}
          onClick={() => onSelect(origIdx)}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(origIdx); } }}
          sx={{
            px: 1.5, py: 1,
            borderRadius: 1.5,
            cursor: 'pointer',
            border: isExpanded ? `2px solid ${BRAND_DARKER}` : '1px solid transparent',
            bgcolor: isExpanded ? '#efeaff' : 'transparent',
            // NO transition — deliberately. The expanded content appears instantly (conditional render),
            // so an eased highlight around it just reads as lag. Instant state = snappy.
            // Pure-CSS hover feedback (no React state — the old state-driven hover re-rendered
            // every row + the whole chart per mousemove):
            ...(!isExpanded && { '&:hover': { border: `1px solid ${BRAND_DARK}`, bgcolor: '#f7f7fa' } }),
            '&:focus-visible': { outline: `2px solid ${BRAND_DARKER}`, outlineOffset: 2 },
          }}
        >
          {/* Compact summary. When the caller opted into hideHeaderOnExpand, the WHOLE strip (incl. the chevron)
              is removed on expand — otherwise its hidden-text container + chevron leave an empty row atop the
              expanded card. The card stays click-to-collapse (onClick on the parent). */}
          <Box sx={{ display: hideHeaderOnExpand && isExpanded ? 'none' : 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ minWidth: 0 }}>
              {/* Date + name: hidden on mobile ONCE EXPANDED (they reappear styled in the detail rows below);
                  always shown when collapsed and on desktop. */}
              <Typography variant="caption" sx={{ color: BRAND_DARKER, fontWeight: 600, fontSize: '0.7rem', display: headerDisplay }}>
                {formatDate(ev.start)}
              </Typography>
              {/* Rows that supply a `provider` (conditions visit-trail) render a STRUCTURED header —
                  "Setting · <b>Dr</b> · Facility" (setting leads, Dr bold). Every other resource just shows `name`. */}
              {ev.provider ? (
                <Typography variant="body2" noWrap sx={{ display: headerDisplay }}>
                  {ev.setting ? `${ev.setting}  ·  ` : ''}
                  <Box component="span" sx={{ fontWeight: 700 }}>{ev.provider}</Box>
                  {ev.facility ? `  ·  ${ev.facility}` : ''}
                </Typography>
              ) : (
                <Typography variant="body2" fontWeight={500} noWrap sx={{ display: headerDisplay }}>
                  {ev.name || `Event ${origIdx + 1}`}
                </Typography>
              )}
              {/* Qualitative result (coded/valueString) for a row with no numeric value — ellipsized to one line;
                  the full text shows on expand (details 'Result'). */}
              {ev.result && (ev.value == null || ev.value === '') && (
                <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', color: '#3a3a3c', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ev.result}
                </Typography>
              )}
              {/* Analyte's OWN flag for THIS reading (Observation.interpretation) — PRIMARY. */}
              {formatInterpretation(ev.interpretation) && (
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: isAbnormalInterp(ev.interpretation) ? '#bd352b' : '#8e8e93' }}>
                  Interpretation: {formatInterpretation(ev.interpretation)}
                </Typography>
              )}
              {/* Panel-level interpretation for THIS reading (2b) — secondary to the analyte's own flag.
                  Neutral grey with the interp word bolded (the panel is NOT the color signal). */}
              {(() => {
                const p = formatPanelLabel(ev.panel);
                return p ? (
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.68rem', color: '#8e8e93' }}>
                    {p.lead}<strong style={{ color: panelInterpColor(p.interp) }}>{p.interp}</strong>.
                  </Typography>
                ) : null;
              })()}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 1 }}>
              {/* "View report" — shown on EVERY row of a group where ANY row has a report, DISABLED where this
                  particular row has none. That's the point: without it you must expand each row to discover
                  whether a report exists. Rendered only when the group has at least one, so groups with no
                  reports at all (the majority) keep the row exactly as before.
                  TOGGLES (Leo): onSelect already toggles (EventTimeline:289) and the report renders INSIDE the
                  expanded row, so closing the report and collapsing the row are the same action — no second
                  piece of state. Label follows suit: View → Hide. stopPropagation keeps the row's own click
                  behaviour untouched, per Leo.
                  On xs it still hides once expanded, matching the rest of the compact strip (the card is
                  tap-to-collapse on mobile, and a lone button in an empty strip was the old "empty top line"). */}
              {groupHasReport && (
                <Button
                  size="small"
                  disabled={!rowHasReport}
                  onClick={(e) => { e.stopPropagation(); onSelect(origIdx); }}
                  sx={{
                    textTransform: 'none', fontSize: '0.7rem', fontWeight: 600, minWidth: 0,
                    px: 0.75, py: 0, mr: 0.5, whiteSpace: 'nowrap',
                    color: BRAND_DARKER, '&.Mui-disabled': { color: '#c7c7cc' },
                    display: { xs: isExpanded ? 'none' : 'inline-flex', sm: 'inline-flex' },
                  }}
                >
                  {!rowHasReport ? 'No report' : isExpanded ? 'Hide report' : 'View report'}
                </Button>
              )}
              {/* Reading value + unit — disambiguates rows that share a date+name (e.g. a count vs a %
                  mis-sharing a LOINC). Numeric obs / med doses populate value+unit; qualitative/other = blank. */}
              {ev.value != null && ev.value !== '' && (
                <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#1c1c1e', mr: 0.5, display: { xs: isExpanded ? 'none' : 'block', sm: 'block' } }}>
                  {ev.value}{ev.unit ? ` ${ev.unit}` : ''}
                </Typography>
              )}
              {/* Chevron hidden on mobile ONCE EXPANDED (like the date/name/value) — the card is click-to-collapse,
                  and leaving a lone chevron in an otherwise-empty compact strip was the "empty line at top" on xs. */}
              <ChevronRightIcon fontSize="small" sx={{
                color: BRAND_DARK,
                transform: isExpanded ? 'rotate(90deg)' : 'none',
                display: { xs: isExpanded ? 'none' : 'block', sm: 'block' },
              }} />
            </Box>
          </Box>

          {/* Expanded detail top spacing. The divider border+margin separates the details from the compact header
              ABOVE — so it's only wanted when that header is visible. It's hidden when: hideHeaderOnExpand (whole
              strip gone, conditions), OR on MOBILE (xs) where date/name/value+chevron are hidden on expand → the
              details are effectively the top of the card. Desktop keeps the divider. Prevents the empty gap at top. */}
          {isExpanded && evDetail && (
            <Box sx={hideHeaderOnExpand
              ? { pt: 0.5 }
              : { mt: { xs: 0.5, sm: 1.5 }, pt: { xs: 0, sm: 1 }, borderTop: { xs: 'none', sm: `1px solid ${BRAND}` } }}>
              {evDetail.primary.map((e, j) => (
                <DetailRow key={`p-${j}`} entry={e} primary last={j === evDetail.primary.length - 1 && evDetail.secondary.length === 0} />
              ))}
              {evDetail.secondary.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Chip size="small"
                    label={showTechnical ? 'Hide technical details' : 'Show technical details'}
                    aria-expanded={showTechnical}
                    onClick={(e) => { e.stopPropagation(); onToggleTechnical(); }}
                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onToggleTechnical(); } }}
                    sx={{ cursor: 'pointer', bgcolor: '#efeaff', fontSize: '0.65rem', height: 22, '&:hover': { bgcolor: '#efeaff' } }}
                  />
                  {showTechnical && (
                    <Box sx={{ mt: 0.75, opacity: 0.7 }}>
                      {evDetail.secondary.map((e, j) => (
                        <DetailRow key={`s-${j}`} entry={e} primary={false} last={j === evDetail.secondary.length - 1} />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Result REPORT document(s) — the procedure's own read, rendered INSIDE this expanded row (Leo
              2026-08-16), below the detail rows and keeping the row's own date/title/status header above.
              The backend sends IDS ONLY (`report[]`, aggregated across the fingerprint group so a report can't
              be lost when its carrier row loses the dedup); DocumentBlock fetches each body on mount — which,
              since it only mounts when expanded, IS the lazy-on-expand behaviour — then renders text/* inline
              (DOMPurify-sanitised) or falls back to a download for a PDF. Several reports stack as separate
              blocks so the user scrolls from one to the next.
              stopPropagation: the row's onClick toggles collapse, and clicking/selecting inside a report
              (or hitting Download) must not close the card underneath it. */}
          {isExpanded && Array.isArray(ev.report) && ev.report.length > 0 && (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${BRAND}` }}
            >
              {ev.report.map((docId: string, j: number) => (
                <DocumentBlock
                  key={docId}
                  id={docId}
                  variant="nested"
                  title={ev.report.length > 1 ? `Report ${j + 1} of ${ev.report.length}` : 'Report'}
                  subtitle={ev.name || undefined}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});
EventRow.displayName = 'EventRow';

// --- Shared Event Timeline View ---
// Used by both GroupedDetailDialog and ChatDataPanel to avoid duplication.
export const EventTimeline: FC<{
  eventList: any[];
  chartData: ChartData | null;
  resource: string;
  groupName: string;
  groupKey?: string;   // analyte KEY for the chart's AI context (backend treats it as key); falls back to groupName
  maxListHeight?: number;
  fillContainer?: boolean;
  growWithContent?: boolean;   // let the list grow to its natural height so a SINGLE ancestor scrollbar (the dialog) owns scrolling — avoids the nested inner scroll (double scrollbar)
  hideHeaderOnExpand?: boolean; // hide the collapsed date/name header on expand (desktop too) — for trails whose details.primary repeat it (conditions visit-trail)
}> = ({ eventList, chartData, resource, groupName, groupKey, maxListHeight, fillContainer, growWithContent, hideHeaderOnExpand }) => {
  // NO hover state — deliberately. It used to live here and every row mouseenter/leave re-rendered
  // this whole component (all rows + the entire Recharts SVG in ValueChart) → the sluggish event list.
  // Row/dot hover feedback is pure CSS (browser-painted, zero React); selection is CLICK-only; and the
  // rows are React.memo'd so a click re-renders only the affected row(s) + the chart (see EventRow).
  const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
  const [showTechnical, setShowTechnical] = useState(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const listRef = useRef<HTMLDivElement>(null);

  // STABLE callbacks (identity never changes) — required for EventRow's memo to hold.
  const onSelect = useCallback((idx: number) => {
    setSelectedEvent(prev => (prev === idx ? null : idx)); // toggle: clicking the open row closes it
    setShowTechnical(false);
  }, []);
  const onToggleTechnical = useCallback(() => setShowTechnical(v => !v), []);

  // Auto-scroll the list to the selected card — fires once per SELECTION (chart-dot click → the list
  // navigates to that event), not on hover.
  useEffect(() => {
    if (selectedEvent !== null && cardRefs.current[selectedEvent]) {
      cardRefs.current[selectedEvent]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedEvent]);

  // Display most recent first; preserve original indices for chart sync. Memoized so row `ev` props
  // stay referentially stable across selection renders (EventRow memo requirement).
  // Does ANY row in this group carry a report? Decided once for the group, not per row: the affordance has to
  // appear on every row (disabled where that row has none) so the reader can see WHICH entries have a report
  // without expanding each one. Groups with no reports at all render the row exactly as before.
  const groupHasReport = useMemo(
    () => eventList.some((ev: any) => Array.isArray(ev?.report) && ev.report.length > 0),
    [eventList],
  );

  const displayList = useMemo(
    () => [...eventList].map((ev, i) => ({ ev, origIdx: i })).reverse(),
    [eventList]
  );

  const Wrapper = fillContainer ? Box : React.Fragment;
  const wrapperProps = fillContainer ? { sx: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } } : {};

  return (
    <Wrapper {...wrapperProps}>
      {/* Optional quantitative value chart */}
      {chartData && (
        <ValueChart
          chart={chartData}
          resource={resource}
          groupName={groupName}
          groupKey={groupKey ?? groupName}
          selectedEvent={selectedEvent}
          onClick={onSelect}
        />
      )}

      <Box ref={listRef} sx={{
        ...(fillContainer
          ? { flex: 1, minHeight: 0, overflowY: 'auto' }
          : growWithContent
            ? { overflowY: 'visible' }                                       // grow naturally — the dialog scrolls
            : { maxHeight: maxListHeight ?? (chartData ? 300 : 450), overflowY: 'auto' }),
        overflowX: 'hidden', pr: 0.5,
      }}>
        {displayList.map(({ ev, origIdx }, displayIdx) => (
          <EventRow
            key={origIdx}
            ev={ev}
            origIdx={origIdx}
            isFirst={displayIdx === 0}
            isLast={displayIdx === displayList.length - 1}
            isExpanded={selectedEvent === origIdx}
            showTechnical={selectedEvent === origIdx && showTechnical}
            onSelect={onSelect}
            groupHasReport={groupHasReport}
            onToggleTechnical={onToggleTechnical}
            cardRefs={cardRefs}
            hideHeaderOnExpand={!!hideHeaderOnExpand}
          />
        ))}
      </Box>
    </Wrapper>
  );
};
